"use client";

import { GitHubRepositoryCard } from "@/components/features/GitHubRepositoryCard";
import { ErrorCard } from "@/components/ui/ErrorCard";
import { LoadingCards } from "@/components/ui/LoadingCards";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API_ENDPOINTS, GITHUB_CONFIG, PAGE_CONTENT } from "@/constants/app.constants";
import { GitHubRepository } from "@/types/github.type";
import { WorkExperienceModel } from "@/types/work-experience.type";
import { useQuery } from "@tanstack/react-query";

const fetchWorkExperience = async (): Promise<WorkExperienceModel[]> => {
  const response = await fetch(API_ENDPOINTS.WORK_EXPERIENCE);
  if (!response.ok) {
    throw new Error("Failed to load work experience");
  }
  return response.json();
};

const fetchGitHubRepos = async (): Promise<GitHubRepository[]> => {
  const response = await fetch(API_ENDPOINTS.GITHUB_REPOS);
  if (!response.ok) {
    throw new Error("Failed to load GitHub repositories");
  }
  return response.json();
};

export default function PortfolioPage() {
  const {
    data: workExperienceData = [],
    isLoading: isWorkLoading,
    isError: isWorkError,
  } = useQuery({
    queryKey: ["work-experience"],
    queryFn: fetchWorkExperience,
  });

  const {
    data: githubRepos = [],
    isLoading: isGithubLoading,
    isError: isGithubError,
  } = useQuery({
    queryKey: ["github-repos"],
    queryFn: fetchGitHubRepos,
  });

  const sortedData = [...workExperienceData].sort((a, b) => b.order - a.order);

  return (
    <>
      <section className="px-5 py-10 max-w-6xl mx-auto">
        <div className="mb-8 animate-fadeIn">
          <h1 className="text-3xl text-primary mb-3 uppercase">{PAGE_CONTENT.PORTFOLIO.TITLE}</h1>
          <p className="text-muted-foreground text-lg">{PAGE_CONTENT.PORTFOLIO.DESCRIPTION}</p>
        </div>

        <Tabs defaultValue="github" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="github">{PAGE_CONTENT.PORTFOLIO.GITHUB_TAB}</TabsTrigger>
            <TabsTrigger value="experience">{PAGE_CONTENT.PORTFOLIO.EXPERIENCE_TAB}</TabsTrigger>
          </TabsList>

          {/* GitHub Projects Tab */}
          <TabsContent value="github" className="space-y-6">
            {isGithubLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <LoadingCards count={6} animationDelay={true} />
              </div>
            ) : isGithubError ? (
              <ErrorCard title="Failed to load GitHub repositories." message={PAGE_CONTENT.PORTFOLIO.GITHUB_ERROR} />
            ) : githubRepos.length === 0 ? (
              <div className="mc-slot p-6 text-center">
                <p className="text-muted-foreground">{PAGE_CONTENT.PORTFOLIO.NO_REPOS}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {githubRepos.map((repo, idx) => (
                  <GitHubRepositoryCard
                    key={repo.id}
                    repo={repo}
                    animationDelay={idx * GITHUB_CONFIG.ANIMATION_DELAY}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Work Experience Tab */}
          <TabsContent value="experience" className="space-y-6">
            {isWorkLoading ? (
              <LoadingCards count={5} animationDelay={true} />
            ) : isWorkError ? (
              <ErrorCard title="Failed to load portfolio." message={PAGE_CONTENT.PORTFOLIO.EXPERIENCE_ERROR} />
            ) : (
              <div className="space-y-6">
                {sortedData.map((experience, idx) => (
                  <div
                    key={experience.id}
                    className="mc-panel p-6 hover:translate-y-[-2px] transition-transform duration-200 animate-slideInUp"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
                      <div>
                        <h2 className="text-xl text-primary">{experience.title}</h2>
                        <p className="text-base text-secondary mt-2 uppercase">{experience.position}</p>
                      </div>
                      <span className="inline-block text-sm text-muted-foreground mc-slot px-4 py-2 whitespace-nowrap">
                        {experience.duration}
                      </span>
                    </div>

                    <p className="text-card-foreground mb-4 leading-relaxed">{experience.description}</p>

                    {experience.projects.length > 0 && (
                      <div className="mt-4">
                        <h3 className="text-primary mb-3 flex items-center gap-2 uppercase text-base">
                          <span className="inline-block w-3 h-3 bg-primary"></span>
                          Projects &amp; Achievements
                        </h3>
                        <ul className="space-y-2">
                          {experience.projects.map((project, idx) => (
                            <li
                              key={idx}
                              className="flex gap-3 text-muted-foreground transition-all duration-200 hover:text-foreground"
                            >
                              <span className="inline-block w-2 h-2 bg-primary shrink-0 mt-2"></span>
                              <span>{project}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>
    </>
  );
}
